import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  inject,
  effect
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { UmssSelectComponent, SelectOption } from '../select/select.component';
import { UmssButtonGroupComponent } from '../button-group/button-group.component';
import { UmssToggleButtonComponent } from '../toggle-button/toggle-button.component';
import { SUPPORTED_SCOPES } from '@umss/shared/core';
import {
  ScopeOfPracticeEnum,
  SCOPE_OF_PRACTICE_ENUM_RECORD,
  SCOPE_OF_PRACTICE_ABBREVIATED_RECORD
} from '@umss/shared/utils';

export type ScopeFilter = 'all' | 'enabled' | 'disabled';
export type ScopeSelectType =
  | ScopeOfPracticeEnum
  | ScopeOfPracticeEnum[]
  | null;

@Component({
  selector: 'umss-scope-of-practice-select',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    UmssSelectComponent,
    UmssButtonGroupComponent,
    UmssToggleButtonComponent
  ],
  template: `
    @if (displayMode === 'select') {
      <umss-select
        [options]="options"
        [placeholder]="placeholder"
        [label]="showLabel ? label : undefined"
        [helperText]="helperText"
        [errorMessage]="errorMessage"
        [size]="size"
        [disabled]="disabled"
        [required]="required"
        [clearable]="true"
        [filterStyle]="filterStyle"
        [searchable]="true"
        [error]="error"
        [loading]="{ selector: loading }"
        [multiple]="multiple"
        [hideSelectedOptions]="hideSelectedOptions"
        [(value)]="value"
        (selectionChange)="onSelectionChange($event)"
        (searchChange)="onSearch($event)"
        (onFocus)="onFocus()"
        (onBlur)="onBlur()"
      />
    } @else {
      <umss-button-group
        [formControl]="internalControl"
        [label]="showLabel ? label : undefined"
        [size]="size"
        [variant]="variant"
        [disabled]="disabled"
        [required]="required"
        [allowWrap]="true"
        [multiple]="multiple"
        [clearable]="true"
        [maxPerRow]="maxPerRow"
      >
        @for (option of _buttonGroupOptions; track option.value) {
          <umss-toggle-button
            [label]="option.label"
            [title]="option.tooltip"
            [value]="option.value"
          />
        }
      </umss-button-group>
    }
  `,
  styles: `
    :host {
      display: contents;
    }

    ::ng-deep .vertical-field {
      flex: 1;
      min-width: 0;
    }
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssScopeOfPracticeSelectComponent),
      multi: true
    }
  ]
})
export class UmssScopeOfPracticeSelectComponent
  implements ControlValueAccessor, OnInit, OnChanges, OnDestroy
{
  private readonly supportedScopesSignal = inject(SUPPORTED_SCOPES, {
    optional: true
  });

  @Input() displayMode: 'select' | 'button-group' = 'select';
  @Input() showLabel: boolean = false;
  @Input() label?: string = 'Scope of Practice';
  @Input() placeholder: string = 'Select a scope of practice';
  @Input() helperText?: string;
  @Input() errorMessage?: string;
  @Input() size: 'Small' | 'Regular' | 'Large' = 'Regular';
  @Input() filterStyle: 'outlined' | 'elevated' = 'outlined';
  @Input() variant: 'outlined' | 'ghost' = 'outlined';
  @Input() disabled = false;
  @Input() required = false;
  @Input() error = false;
  @Input() loading = false;
  @Input() multiple: boolean = false;
  @Input() hideSelectedOptions: boolean = false;
  @Input() maxPerRow?: number;
  @Input() labelMode: 'full' | 'abbreviated' | 'auto' = 'auto';

  @Input() scopeOfPracticeFilter: ScopeFilter = 'all';

  @Input() set value(val: ScopeSelectType) {
    this._value = val;
  }
  get value(): ScopeSelectType {
    return this._value;
  }

  @Output() valueChange = new EventEmitter<ScopeSelectType>();

  options: SelectOption[] = [];
  readonly internalControl = new FormControl<ScopeSelectType>(null);

  get _effectiveLabelMode(): 'full' | 'abbreviated' {
    if (this.labelMode === 'auto')
      return this.displayMode === 'button-group' ? 'abbreviated' : 'full';

    return this.labelMode;
  }

  get _buttonGroupOptions(): Array<{
    value: ScopeOfPracticeEnum;
    label: string;
    tooltip: string | undefined;
  }> {
    const abbreviated = this._effectiveLabelMode === 'abbreviated';
    return this.options.map((opt) => ({
      value: opt.value as ScopeOfPracticeEnum,
      label: abbreviated
        ? (SCOPE_OF_PRACTICE_ABBREVIATED_RECORD[
            opt.value as ScopeOfPracticeEnum
          ] ?? opt.label)
        : opt.label,
      tooltip: abbreviated ? opt.label : undefined
    }));
  }

  private filteredOptions: SelectOption[] = [];
  private _value: ScopeSelectType = null;
  private _internalSub?: Subscription;

  constructor() {
    effect(() => {
      // Re-initialize options when the supported scopes signal changes
      if (this.supportedScopesSignal) {
        this.supportedScopesSignal();
        if (['enabled', 'disabled'].includes(this.scopeOfPracticeFilter)) {
          this.initializeOptions();
        }
      }
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars
  private _onChange = (value: ScopeSelectType) => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private _onTouched = () => {};

  ngOnInit(): void {
    this.initializeOptions();
    this._internalSub = this.internalControl.valueChanges.subscribe((value) => {
      this._value = value;
      this._onChange(value);
      this._onTouched();
      this.valueChange.emit(value);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['scopeOfPracticeFilter']) {
      this.initializeOptions();
    }
  }

  ngOnDestroy(): void {
    this._internalSub?.unsubscribe();
  }

  private initializeOptions(): void {
    const supportedScopes = this.supportedScopesSignal?.() ?? [];
    const allOptions = Object.entries(SCOPE_OF_PRACTICE_ENUM_RECORD).map(
      ([value, label]) => ({
        value: value as ScopeOfPracticeEnum,
        label: label
      })
    );

    switch (this.scopeOfPracticeFilter) {
      case 'enabled':
        this.filteredOptions = allOptions.filter((option) =>
          supportedScopes.includes(option.value)
        );
        break;

      case 'disabled':
        this.filteredOptions = allOptions.filter(
          (option) => !supportedScopes.includes(option.value)
        );
        break;

      case 'all':
      default:
        this.filteredOptions = allOptions;
        break;
    }

    this.options = [...this.filteredOptions];
  }

  writeValue(value: ScopeSelectType): void {
    this._value = value;
    this.internalControl.setValue(value, { emitEvent: false });
  }

  registerOnChange(fn: (value: ScopeSelectType) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onSelectionChange(value: ScopeSelectType): void {
    this._value = value;
    this._onChange(value);
    this._onTouched();
    this.valueChange.emit(value);
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onFocus(): void {}

  onSearch(value: string | null): void {
    if (!value) {
      this.options = [...this.filteredOptions];
    } else {
      const query = value.toLowerCase();
      this.options = this.filteredOptions.filter((option) => {
        const fullName = option.label.toLowerCase();
        const abbreviated = (
          SCOPE_OF_PRACTICE_ABBREVIATED_RECORD[
            option.value as ScopeOfPracticeEnum
          ] ?? ''
        ).toLowerCase();
        return fullName.includes(query) || abbreviated.includes(query);
      });
    }
  }

  onBlur(): void {
    this._onTouched();
  }
}
